import {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {useRouteMatch, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../i18n/translations';
import {useOffice} from '../../../../../../../contexts/office/context';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {CaregiverMode, ScreenPaths} from '../../../../../../../enums';
import {PlaylistItem} from '../../../../../../../backend/interfaces/playlist-item';
import {Playlist} from '../../../../../../../backend/interfaces/playlist';
import {PlaylistItemForm} from '../../../../../../../components/playlist-form/playlist-item-form';

export default function SeniorPlaylistItemProfessionalCaregiverOfficeScreen() {
  let {
    id,
    entityId,
    playlistId,
    itemId,
  }: {id: string; entityId: string; playlistId: string; itemId: string} = useParams();
  const [playlist, setPlaylist] = useState<Playlist | undefined>();
  const [playlistItem, setPlaylistItem] = useState<PlaylistItem | undefined>();
  const {t} = useTranslation();
  const {url} = useRouteMatch();
  const officeContext = useOffice();
  const goBackUrl = url.replace(`/items/${itemId}`, '');
  const isLoading = !playlistItem || !playlist;
  const userName = (officeContext.state.userForm?.user.displayName ||
    officeContext.state.userForm?.user.name)!;

  useEffect(() => {
    const getPlaylist = async () => {
      try {
        // Get the playlist
        const result =
          await PlaylistsRepository.instance.getPlaylistByIdForEntityAsAffiliatedForEntity(
            entityId,
            playlistId,
            true
          );

        if (result) {
          // Set playlist
          setPlaylist(result);
        } else {
          // Error
          throw new Error('Empty playlist.');
        }
      } catch (error) {
        // Log
        console.debug('🔴 Could not get the playlist.', error);
      }
    };

    // Get the playlist
    getPlaylist();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistId]);

  useEffect(() => {
    const getPlaylistItem = async () => {
      try {
        if (!entityId || !playlistId || !itemId) {
          throw new Error('Empty entity, playlist, or playlist item id.');
        }

        const result =
          await PlaylistsRepository.instance.getPlaylistItemByIdForEntityAsAffiliatedForEntity(
            entityId,
            playlistId,
            itemId,
            false
          );

        if (result) {
          officeContext.setPlaylistItemForm(result);
          setPlaylistItem(result);
        } else {
          // Error
          throw new Error('Empty playlist item.');
        }
      } catch (error) {
        console.debug('🔴 Could not get the playlist item.', error);
      }
    };

    getPlaylistItem();

    return () => {
      officeContext.clearPlaylistItemForm();
      console.debug('🧹 Playlist item form cleaned in office context.');
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.screen_paths.professional_caregiver_seniors),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}`,
          },
          {
            label: userName || '',
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_PersonalData}${ScreenPaths.Senior_Profile}`,
          },
          {
            label: t(translations.role_permissions_section['digital-inclusion']),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_DigitalInclusion}`,
          },
          {
            label: t(translations.screen_paths.senior_playlists),
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Playlists}`,
          },
          {
            label: playlist?.name || '',
            link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_Seniors}/${id}${ScreenPaths.Senior_DigitalInclusion}${ScreenPaths.Senior_Playlists}/${playlistId}`,
          },
          {
            label: playlistItem?.title || '',
            link: url,
          },
        ]}
        isLoading={isLoading}
        leftColumn={<></>}
        rightColumn={<></>}
      />
      <Box className="listing-container">
        <Box className="listing-container-centered">
          <PlaylistItemForm
            caregiverMode={CaregiverMode.Professional}
            canWrite={false}
            showExitButton={true}
            showPageTitle={false}
            redirectUrl={goBackUrl}
            entityId={entityId}
          />
        </Box>
      </Box>
    </Box>
  );
}
